import React from 'react';
import { motion } from 'framer-motion';

/**
 * ScrollAnimate component to animate elements as they scroll into view.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The content to animate
 * @param {'up' | 'down' | 'left' | 'right' | 'none'} [props.direction='up'] - Direction of the slide
 * @param {number} [props.delay=0] - Delay before animation starts in seconds
 * @param {number} [props.duration=0.8] - Duration of the animation in seconds
 * @param {string} [props.className=""] - Additional CSS classes
 * @param {boolean} [props.scale=false] - Whether to slightly scale up from 0.95 to 1
 * @param {string} [props.margin="-100px"] - Viewport margin for triggering the animation
 */
export default function ScrollAnimate({ 
  children, 
  direction = 'up', 
  delay = 0, 
  duration = 0.8, 
  className = "",
  scale = false,
  margin = "0px 0px -50px 0px"
}) {
  const directions = {
    up: { y: 30, x: 0 },
    down: { y: -30, x: 0 },
    left: { x: 30, y: 0 },
    right: { x: -30, y: 0 },
    none: { x: 0, y: 0 }
  };

  const initialY = directions[direction]?.y ?? 0;
  const initialX = directions[direction]?.x ?? 0;

  return (
    <motion.div
      initial={{ 
        opacity: 0, 
        y: initialY, 
        x: initialX,
        scale: scale ? 0.96 : 1
      }}
      whileInView={{ 
        opacity: 1, 
        y: 0, 
        x: 0,
        scale: 1
      }}
      viewport={{ once: true, margin: margin }}
      transition={{ 
        duration: duration, 
        delay: delay,
        ease: [0.21, 0.85, 0.45, 1] // Premium smooth cubic-bezier curve
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
